import { Dialog } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  ActiveProviderData,
  CreateProviderAppointmentResponse,
  MenuItem,
} from '@models';
import {
  ActiveProviderService,
  ActiveTelehealthCallService,
  ProviderAppointmentsNotificationService,
  ToastService,
} from '@services';
import {
  BookAppointmentProviderDialogComponent,
  DropdownMenuComponent,
  ProviderDependantHeader,
  TelehealthLiveControlComponent,
} from '@ui-lib';
import { HelpCenterDialogComponent } from '../help-center-dialog/help-center-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { catchError, EMPTY, finalize } from 'rxjs';
import { InvitePatientModalService } from '../../patient-list/services/invite-patient-modal.service';
import { AskAiInputComponent } from '../ask-ai-input/ask-ai-input.component';

@Component({
  selector: 'provider-portal-portal-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    AskAiInputComponent,
    TelehealthLiveControlComponent,
    DropdownMenuComponent,
  ],
  templateUrl: './provider-portal-header.component.html',
})
export class ProviderPortalHeaderComponent
  implements OnInit, ProviderDependantHeader
{
  private readonly dialog = inject(Dialog);
  private readonly toast = inject(ToastService);
  private readonly invitePatientModal = inject(InvitePatientModalService);
  private readonly providerAppointmentsNotificationService = inject(
    ProviderAppointmentsNotificationService
  );
  private readonly activeProviderService = inject(ActiveProviderService);
  private readonly router = inject(Router);
  protected readonly telehealthCall = inject(ActiveTelehealthCallService);

  loadingProvider = signal(true);
  activeProvider = signal<ActiveProviderData | null>(null);

  activeProviderLoaded = output<ActiveProviderData>();

  protected readonly headerMenuItems = computed<MenuItem[]>(() => [
    { id: 'invite-patient', label: 'Invite patient', icon: 'plus' },
    {
      id: 'new-appointment',
      label: 'New Appointment',
      icon: 'plus',
      isDisabled: this.loadingProvider(),
    },
    { id: 'support', label: 'Support', icon: 'support' },
  ]);

  protected goToPatientPage(): void {
    const patientId = this.telehealthCall.activeAppointment()?.patientId;
    if (patientId) {
      void this.router.navigate(['patient-list', patientId, 'chart-notes']);
    }
  }

  protected onHeaderAction(id: string): void {
    switch (id) {
      case 'invite-patient':
        this.handleInvitePatient();
        break;
      case 'new-appointment':
        void this.openNewAppointmentDialog();
        break;
      case 'support':
        this.openHelpCenterDialog();
        break;
    }
  }

  ngOnInit(): void {
    this.activeProviderService
      .getActiveProviderData()
      .pipe(finalize(() => this.loadingProvider.set(false)))
      .subscribe(data => {
        this.activeProvider.set(data);

        queueMicrotask(() => {
          this.activeProviderLoaded.emit(data);
        });
      });
  }

  async openNewAppointmentDialog(): Promise<void> {
    const activeProvider = this.activeProvider();
    if (!activeProvider) return;

    try {
      this.dialog
        .open<CreateProviderAppointmentResponse>(
          BookAppointmentProviderDialogComponent,
          {
            data: { activeProvider },
          }
        )
        .closed.pipe(
          catchError(() => {
            this.toast.showWarning('Failed to book an appointment.');
            return EMPTY;
          })
        )
        .subscribe(result => {
          if (result) {
            this.providerAppointmentsNotificationService.emit(result);
            this.toast.showSuccess('Your appointment is booked.');
          }
        });
    } catch (error) {
      console.error(
        'Failed to load BookAppointmentProviderDialogComponent:',
        error
      );
    }
  }

  handleInvitePatient() {
    this.invitePatientModal.open().subscribe();
  }

  openHelpCenterDialog(): void {
    this.dialog.open(HelpCenterDialogComponent);
  }
}
